import { useState } from 'react';
import { Drawer, Box, Typography, IconButton, TextField, Button, Alert } from '@mui/material';
import { X } from 'lucide-react';
import { useCreatePortfolioCompany } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';

interface AddPortfolioCompanyDrawerProps {
  open: boolean;
  onClose: () => void;
  fundId: string;
  tenantId: string;
}

export function AddPortfolioCompanyDrawer({ open, onClose, fundId, tenantId }: AddPortfolioCompanyDrawerProps) {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [sector, setSector] = useState('');
  const [investmentDate, setInvestmentDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: currentUser } = useCurrentUser();
  const createCompany = useCreatePortfolioCompany();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !currentUser) return;

    try {
      await createCompany.mutateAsync({
        tenant_id: tenantId,
        fund_id: fundId,
        name: name.trim(),
        website: website.trim() || null,
        sector: sector.trim() || null,
        investment_date: investmentDate || null,
      });

      // Reset form
      setName('');
      setWebsite('');
      setSector('');
      setInvestmentDate('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add portfolio company');
    }
  };

  const handleClose = () => {
    setName('');
    setWebsite('');
    setSector('');
    setInvestmentDate('');
    setError(null);
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} sx={{ '& .MuiDrawer-paper': { width: 500 } }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Add Portfolio Company</Typography>
          <IconButton onClick={handleClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}

            <TextField autoFocus label="Company Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Acme Corporation" required />

            <TextField label="Website" fullWidth value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" />

            <TextField label="Sector" fullWidth value={sector} onChange={(e) => setSector(e.target.value)} placeholder="e.g., Technology, Healthcare" />

            <TextField
              label="Investment Date"
              type="date"
              fullWidth
              value={investmentDate}
              onChange={(e) => setInvestmentDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button onClick={handleClose} disabled={createCompany.isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={createCompany.isPending || !name.trim()}>
                {createCompany.isPending ? 'Adding...' : 'Add Company'}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
}
